import { useMemo } from "react";
import { ICharityMmsSetup } from "../../../../../../utils/firebase/mmsSetup";

export type MmsDisplayState =
  | "progress"
  | "paused"
  | "processing"
  | "attention"
  | "completed"
  | "failed";

export interface MmsProgress {
  completedSteps: number;
  totalSteps: number;
  isLoaded: boolean;
  displayState: MmsDisplayState;
  needsAttention: boolean;
}

export const useMmsProgress = (
  charityId: string,
  mmsSetup: ICharityMmsSetup | null,
  mmsSetupLoaded: boolean,
  needsAttention = false,
  pipelineRunning = false,
  statusLoadError: string | null = null,
  setupFailed = false
): MmsProgress => {
  return useMemo(() => {
    const totalSteps = 3;

    if (!mmsSetupLoaded) {
      return {
        completedSteps: 0,
        totalSteps,
        isLoaded: false,
        displayState: "progress",
        needsAttention: false,
      };
    }

    if (mmsSetup?.error || setupFailed) {
      return {
        completedSteps: 1,
        totalSteps,
        isLoaded: true,
        displayState: "attention",
        needsAttention: true,
      };
    }

    if (statusLoadError) {
      return {
        completedSteps: 1,
        totalSteps,
        isLoaded: true,
        displayState: "attention",
        needsAttention: true,
      };
    }

    if (mmsSetup?.status === "completed" && mmsSetup?.mmsSenderAreaCode) {
      return {
        completedSteps: totalSteps,
        totalSteps,
        isLoaded: true,
        displayState: "completed",
        needsAttention: false,
      };
    }

    if (mmsSetup?.status === "pending" && mmsSetup?.mmsSenderAreaCode) {
      if (needsAttention) {
        return {
          completedSteps: 2,
          totalSteps,
          isLoaded: true,
          displayState: "attention",
          needsAttention: true,
        };
      }
      if (pipelineRunning) {
        return {
          completedSteps: 2,
          totalSteps,
          isLoaded: true,
          displayState: "processing",
          needsAttention: false,
        };
      }
      return {
        completedSteps: 1,
        totalSteps,
        isLoaded: true,
        displayState: "paused",
        needsAttention: false,
      };
    }

    return {
      completedSteps: 0,
      totalSteps,
      isLoaded: true,
      displayState: "progress",
      needsAttention: false,
    };
  }, [mmsSetup, mmsSetupLoaded, needsAttention, pipelineRunning, statusLoadError, setupFailed]);
};
